import { FC, useCallback, useEffect, useMemo, useState } from 'react'
import Swal from 'sweetalert2'
import { PageTitle } from '../../../../../_metronic/layout/core'
import useAuth from '../../../../hooks/useAuth'
import { getSessionHistory } from '../../SellerQueue/services'
import { SellerServiceSessionItem } from '../../SellerQueue/types'
import { ReportShell } from '../components/ReportShell'
import { SellerIdentity } from '../components/SellerIdentity'
import { createDefaultSellerReportDateRange, useSellerReportFilters } from '../useSellerReportFilters'
import * as XLSX from 'xlsx'
import jsPDF from 'jspdf'
import autoTable from 'jspdf-autotable'

interface SellerSlaRow {
  sellerId: number
  sellerLabel: string
  totalSessions: number
  waitSlaHitCount: number
  serviceSlaHitCount: number
  avgWaitSeconds: number
  avgServiceSeconds: number
  waitSlaRate: number
  serviceSlaRate: number
}

interface SlaSummary {
  totalSessions: number
  avgWaitSeconds: number
  avgServiceSeconds: number
  waitSlaRate: number
  serviceSlaRate: number
}

const numberFormatter = new Intl.NumberFormat('pt-BR')
const percentFormatter = new Intl.NumberFormat('pt-BR', {
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
})


const getSellerLabel = (session: SellerServiceSessionItem): string => {
  const fallbackName = `Vendedor ${session.sellerId}`
  const rawName = session.sellerName?.trim()

  return rawName && rawName.length > 0 ? rawName : fallbackName
}

const parseDate = (value?: string | null): Date | null => {
  if (!value) {
    return null
  }

  const date = new Date(value)
  if (Number.isNaN(date.getTime())) {
    return null
  }

  return date
}

const formatSeconds = (seconds: number): string => {
  const normalized = Math.max(0, Math.round(seconds))
  const minutes = Math.floor(normalized / 60)
  const remainingSeconds = normalized % 60

  if (minutes === 0) {
    return `${remainingSeconds}s`
  }

  return `${minutes}m ${String(remainingSeconds).padStart(2, '0')}s`
}

const calcRate = (hits: number, total: number): number => {
  if (!total) {
    return 0
  }

  return (hits / total) * 100
}

const exportToCSV = (filename: string, rows: SellerSlaRow[], summary: SlaSummary): void => {
  let csv = 'sep=;\n'
  csv += 'Posição;Vendedor;Atendimentos;Tempo médio espera;Tempo médio atendimento;SLA espera (%);SLA atendimento (%)\n'

  rows.forEach((row, index) => {
    csv += `${index + 1};"${row.sellerLabel}";${row.totalSessions};${(row.avgWaitSeconds / 60).toFixed(1)};${(row.avgServiceSeconds / 60).toFixed(1)};${row.waitSlaRate.toFixed(1)};${row.serviceSlaRate.toFixed(1)}\n`
  })

  csv += '\nRESUMO\n'
  csv += 'Atendimentos;Tempo medio espera;Tempo medio atendimento;SLA espera (%);SLA atendimento (%)\n'
  csv += `${summary.totalSessions};${(summary.avgWaitSeconds / 60).toFixed(1)};${(summary.avgServiceSeconds / 60).toFixed(1)};${summary.waitSlaRate.toFixed(1)};${summary.serviceSlaRate.toFixed(1)}\n`

  const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' })
  const link = document.createElement('a')
  const url = URL.createObjectURL(blob)
  link.setAttribute('href', url)
  link.setAttribute('download', filename)
  link.style.visibility = 'hidden'
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
}

const exportToXLSX = (filename: string, rows: SellerSlaRow[], summary: SlaSummary): void => {
  const workbook = XLSX.utils.book_new()
  const sheet = XLSX.utils.json_to_sheet(
    rows.map((row, index) => ({
      Posição: index + 1,
      Vendedor: row.sellerLabel,
      Atendimentos: row.totalSessions,
      'Tempo médio espera (min)': (row.avgWaitSeconds / 60).toFixed(1),
      'Tempo médio atendimento (min)': (row.avgServiceSeconds / 60).toFixed(1),
      'SLA espera (%)': row.waitSlaRate.toFixed(1),
      'SLA atendimento (%)': row.serviceSlaRate.toFixed(1),
    }))
  )
  const summarySheet = XLSX.utils.json_to_sheet([
    {
      Atendimentos: summary.totalSessions,
      'Tempo médio espera (min)': (summary.avgWaitSeconds / 60).toFixed(1),
      'Tempo médio atendimento (min)': (summary.avgServiceSeconds / 60).toFixed(1),
      'SLA espera (%)': summary.waitSlaRate.toFixed(1),
      'SLA atendimento (%)': summary.serviceSlaRate.toFixed(1),
    },
  ])

  XLSX.utils.book_append_sheet(workbook, summarySheet, 'Resumo')
  XLSX.utils.book_append_sheet(workbook, sheet, 'Por Vendedor')
  XLSX.writeFile(workbook, filename)
}

const exportToPDF = (filename: string, rows: SellerSlaRow[], summary: SlaSummary): void => {
  const pdf = new jsPDF()
  const pageWidth = pdf.internal.pageSize.getWidth()
  const pageHeight = pdf.internal.pageSize.getHeight()
  const margin = 15
  let yPosition = margin

  pdf.setFontSize(16)
  pdf.text('Relatório de SLA de Atendimento', margin, yPosition)
  yPosition += 8

  pdf.setFontSize(10)
  pdf.setTextColor(100, 100, 100)
  pdf.text(`Relatório gerado em: ${new Date().toLocaleString('pt-BR')}`, margin, yPosition)
  yPosition += 12

  pdf.setFontSize(11)
  pdf.setTextColor(0, 0, 0)
  pdf.text('Resumo', margin, yPosition)
  yPosition += 6

  pdf.setFontSize(9)
  pdf.text(`Atendimentos: ${summary.totalSessions} | SLA espera: ${summary.waitSlaRate.toFixed(1)}% | SLA atendimento: ${summary.serviceSlaRate.toFixed(1)}%`, margin, yPosition)
  yPosition += 10

  autoTable(pdf, {
    startY: yPosition,
    head: [['Pos', 'Vendedor', 'Atendimentos', 'Espera (min)', 'Atendimento (min)', 'SLA espera (%)', 'SLA atendimento (%)']],
    body: rows.map((row, index) => [
      index + 1,
      row.sellerLabel,
      row.totalSessions,
      (row.avgWaitSeconds / 60).toFixed(1),
      (row.avgServiceSeconds / 60).toFixed(1),
      row.waitSlaRate.toFixed(1),
      row.serviceSlaRate.toFixed(1),
    ]),
    margin: { left: margin, right: margin },
    headStyles: { fillColor: [41, 128, 185], textColor: 255, fontSize: 9, fontStyle: 'bold' },
    bodyStyles: { fontSize: 8, textColor: 50 },
    alternateRowStyles: { fillColor: [245, 245, 245] },
  })

  const pageCount = (pdf as any).internal.getNumberOfPages()
  for (let i = 1; i <= pageCount; i++) {
    pdf.setPage(i)
    pdf.setFontSize(8)
    pdf.setTextColor(150, 150, 150)
    pdf.text(`Página ${i} de ${pageCount}`, pageWidth / 2, pageHeight - 10, { align: 'center' })
  }

  pdf.save(filename)
}

const SellerAttendanceSlaReportPage: FC = () => {
  const { loginInfo } = useAuth()
  const pluginspaceId = Number(loginInfo.pluginspaceId || 0)

  const { filters, setFilters, isReady } = useSellerReportFilters({
    ...createDefaultSellerReportDateRange(),
    waitSlaMinutes: 2,
    serviceSlaMinutes: 10,
  })
  const [loading, setLoading] = useState(false)
  const [sessions, setSessions] = useState<SellerServiceSessionItem[]>([])

  const loadReport = useCallback(async () => {
    if (!pluginspaceId || pluginspaceId <= 0) {
      setSessions([])
      return
    }

    try {
      setLoading(true)
      const data = await getSessionHistory(pluginspaceId, filters.startDate, filters.finalDate)
      setSessions(data)
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Erro ao carregar relatório',
        text: error instanceof Error ? error.message : 'Erro desconhecido',
        confirmButtonText: 'OK',
      })
    } finally {
      setLoading(false)
    }
  }, [filters.finalDate, filters.startDate, pluginspaceId])

  useEffect(() => {
    if (isReady) {
      void loadReport()
    }
  }, [isReady, loadReport])

  const normalizedSessions = useMemo(() => {
    return sessions
      .map((session) => {
        const createdAt = parseDate(session.updated)
        const startedAt = parseDate(session.startedAt)

        if (!createdAt || !startedAt) {
          return null
        }

        const waitSeconds = Math.max(0, Math.round((startedAt.getTime() - createdAt.getTime()) / 1000))
        const serviceSeconds = Math.max(0, Number(session.durationSeconds || 0))

        return {
          ...session,
          waitSeconds,
          serviceSeconds,
        }
      })
      .filter((session): session is SellerServiceSessionItem & { waitSeconds: number; serviceSeconds: number } => {
        return Boolean(session)
      })
  }, [sessions])

  const sellerRows = useMemo<SellerSlaRow[]>(() => {
    const waitSlaSeconds = Math.max(0, filters.waitSlaMinutes) * 60
    const serviceSlaSeconds = Math.max(0, filters.serviceSlaMinutes) * 60
    const grouped = new Map<number, SellerSlaRow>()

    normalizedSessions.forEach((session) => {
      const existing = grouped.get(session.sellerId)
      const waitHit = session.waitSeconds <= waitSlaSeconds ? 1 : 0
      const serviceHit = session.serviceSeconds <= serviceSlaSeconds ? 1 : 0

      if (existing) {
        existing.totalSessions += 1
        existing.waitSlaHitCount += waitHit
        existing.serviceSlaHitCount += serviceHit
        existing.avgWaitSeconds += session.waitSeconds
        existing.avgServiceSeconds += session.serviceSeconds
        return
      }

      grouped.set(session.sellerId, {
        sellerId: session.sellerId,
        sellerLabel: getSellerLabel(session),
        totalSessions: 1,
        waitSlaHitCount: waitHit,
        serviceSlaHitCount: serviceHit,
        avgWaitSeconds: session.waitSeconds,
        avgServiceSeconds: session.serviceSeconds,
        waitSlaRate: 0,
        serviceSlaRate: 0,
      })
    })

    const rows = Array.from(grouped.values()).map((row) => {
      const avgWaitSeconds = row.totalSessions ? row.avgWaitSeconds / row.totalSessions : 0
      const avgServiceSeconds = row.totalSessions ? row.avgServiceSeconds / row.totalSessions : 0

      return {
        ...row,
        avgWaitSeconds,
        avgServiceSeconds,
        waitSlaRate: calcRate(row.waitSlaHitCount, row.totalSessions),
        serviceSlaRate: calcRate(row.serviceSlaHitCount, row.totalSessions),
      }
    })

    rows.sort((a, b) => {
      if (b.waitSlaRate !== a.waitSlaRate) {
        return b.waitSlaRate - a.waitSlaRate
      }
      return b.totalSessions - a.totalSessions
    })

    return rows
  }, [filters.serviceSlaMinutes, filters.waitSlaMinutes, normalizedSessions])

  const summary = useMemo<SlaSummary>(() => {
    if (!normalizedSessions.length) {
      return {
        totalSessions: 0,
        avgWaitSeconds: 0,
        avgServiceSeconds: 0,
        waitSlaRate: 0,
        serviceSlaRate: 0,
      }
    }

    const waitSlaSeconds = Math.max(0, filters.waitSlaMinutes) * 60
    const serviceSlaSeconds = Math.max(0, filters.serviceSlaMinutes) * 60

    let waitTotal = 0
    let serviceTotal = 0
    let waitSlaHitCount = 0
    let serviceSlaHitCount = 0

    normalizedSessions.forEach((session) => {
      waitTotal += session.waitSeconds
      serviceTotal += session.serviceSeconds

      if (session.waitSeconds <= waitSlaSeconds) {
        waitSlaHitCount += 1
      }

      if (session.serviceSeconds <= serviceSlaSeconds) {
        serviceSlaHitCount += 1
      }
    })

    return {
      totalSessions: normalizedSessions.length,
      avgWaitSeconds: waitTotal / normalizedSessions.length,
      avgServiceSeconds: serviceTotal / normalizedSessions.length,
      waitSlaRate: calcRate(waitSlaHitCount, normalizedSessions.length),
      serviceSlaRate: calcRate(serviceSlaHitCount, normalizedSessions.length),
    }
  }, [filters.serviceSlaMinutes, filters.waitSlaMinutes, normalizedSessions])

  return (
    <>
      <PageTitle
        breadcrumbs={[
          { title: 'SLA de Atendimento por Vendedor', path: '/seller/reports/attendance-sla', isActive: true },
        ]}
      >
        Relatório de SLA de Atendimento
      </PageTitle>

      <ReportShell
        title='SLA de Atendimento por Vendedor'
        description='Monitora tempo de espera e tempo de atendimento por vendedor, com taxa de cumprimento dos SLAs definidos.'
        actions={
          <div className='d-flex gap-2'>
            <button className='btn btn-primary' onClick={loadReport} disabled={loading}>
              <i className='fas fa-sync-alt me-2'></i>
              {loading ? 'Carregando...' : 'Atualizar'}
            </button>
            <button className='btn btn-light-success' onClick={() => {
              const timestamp = new Date().toISOString().split('T')[0]
              exportToCSV(`SLA-Atendimento-${timestamp}.csv`, sellerRows, summary)
            }} disabled={loading || sellerRows.length === 0} title='Baixar relatório em CSV'>
              <i className='fas fa-file-csv me-2'></i> CSV
            </button>
            <button className='btn btn-light-info' onClick={() => {
              const timestamp = new Date().toISOString().split('T')[0]
              exportToXLSX(`SLA-Atendimento-${timestamp}.xlsx`, sellerRows, summary)
            }} disabled={loading || sellerRows.length === 0} title='Baixar relatório em XLSX'>
              <i className='fas fa-file-excel me-2'></i> XLSX
            </button>
            <button className='btn btn-light-danger' onClick={() => {
              const timestamp = new Date().toISOString().split('T')[0]
              exportToPDF(`SLA-Atendimento-${timestamp}.pdf`, sellerRows, summary)
            }} disabled={loading || sellerRows.length === 0} title='Baixar relatório em PDF'>
              <i className='fas fa-file-pdf me-2'></i> PDF
            </button>
          </div>
        }
        filters={
          <div className='row g-3 align-items-end'>
            <div className='col-12 col-md-3'>
              <label className='form-label fw-semibold'>Data inicial</label>
              <input
                className='form-control'
                type='date'
                value={filters.startDate}
                onChange={(event) =>
                  setFilters((previous) => ({ ...previous, startDate: event.target.value }))
                }
              />
            </div>
            <div className='col-12 col-md-3'>
              <label className='form-label fw-semibold'>Data final</label>
              <input
                className='form-control'
                type='date'
                value={filters.finalDate}
                onChange={(event) =>
                  setFilters((previous) => ({ ...previous, finalDate: event.target.value }))
                }
              />
            </div>
            <div className='col-12 col-md-2'>
              <label className='form-label fw-semibold'>SLA espera (min)</label>
              <input
                className='form-control'
                type='number'
                min={0}
                value={filters.waitSlaMinutes}
                onChange={(event) =>
                  setFilters((previous) => ({
                    ...previous,
                    waitSlaMinutes: Math.max(0, Number(event.target.value || 0)),
                  }))
                }
              />
            </div>
            <div className='col-12 col-md-2'>
              <label className='form-label fw-semibold'>SLA atendimento (min)</label>
              <input
                className='form-control'
                type='number'
                min={0}
                value={filters.serviceSlaMinutes}
                onChange={(event) =>
                  setFilters((previous) => ({
                    ...previous,
                    serviceSlaMinutes: Math.max(0, Number(event.target.value || 0)),
                  }))
                }
              />
            </div>
            <div className='col-12 col-md-2 d-grid'>
              <button className='btn btn-light-primary' onClick={loadReport} disabled={loading}>
                Buscar
              </button>
            </div>
          </div>
        }
      >
        {!pluginspaceId ? (
          <div className='alert alert-warning mb-0'>
            Pluginspace nao identificado para o usuario logado.
          </div>
        ) : (
          <>
            <div className='row g-4 mb-4'>
              <div className='col-12 col-md-6 col-xl-3'>
                <div className='card border-0 shadow-sm h-100'>
                  <div className='card-body'>
                    <div className='text-gray-600 fw-semibold mb-1'>Total de atendimentos</div>
                    <div className='fs-2hx fw-bold text-primary'>
                      {numberFormatter.format(summary.totalSessions)}
                    </div>
                  </div>
                </div>
              </div>

              <div className='col-12 col-md-6 col-xl-3'>
                <div className='card border-0 shadow-sm h-100'>
                  <div className='card-body'>
                    <div className='text-gray-600 fw-semibold mb-1'>Tempo medio de espera</div>
                    <div className='fs-2 fw-bold text-dark'>{formatSeconds(summary.avgWaitSeconds)}</div>
                  </div>
                </div>
              </div>

              <div className='col-12 col-md-6 col-xl-3'>
                <div className='card border-0 shadow-sm h-100'>
                  <div className='card-body'>
                    <div className='text-gray-600 fw-semibold mb-1'>Cumprimento SLA espera</div>
                    <div className='fs-2 fw-bold text-success'>
                      {percentFormatter.format(summary.waitSlaRate)}%
                    </div>
                  </div>
                </div>
              </div>

              <div className='col-12 col-md-6 col-xl-3'>
                <div className='card border-0 shadow-sm h-100'>
                  <div className='card-body'>
                    <div className='text-gray-600 fw-semibold mb-1'>Cumprimento SLA atendimento</div>
                    <div className='fs-2 fw-bold text-info'>
                      {percentFormatter.format(summary.serviceSlaRate)}%
                    </div>
                    <div className='text-gray-700 mt-2'>Tempo medio: {formatSeconds(summary.avgServiceSeconds)}</div>
                  </div>
                </div>
              </div>
            </div>

            <div className='card border-0 shadow-sm'>
              <div className='card-header border-0 pt-5'>
                <div className='card-title d-flex flex-column'>
                  <span className='fs-3 fw-bold text-dark'>SLA por vendedor</span>
                  <span className='text-gray-600 mt-1'>
                    Espera considera diferença entre atualização da sessão e início de atendimento.
                  </span>
                </div>
              </div>
              <div className='card-body pt-0'>
                {loading ? (
                  <div className='d-flex justify-content-center align-items-center py-10'>
                    <div className='spinner-border text-primary' role='status'>
                      <span className='visually-hidden'>Carregando...</span>
                    </div>
                  </div>
                ) : sellerRows.length > 0 ? (
                  <div className='table-responsive'>
                    <table className='table align-middle table-row-dashed fs-6 gy-3'>
                      <thead>
                        <tr className='text-start text-gray-600 fw-bold fs-7 text-uppercase gs-0'>
                          <th>Posicao</th>
                          <th>Vendedor</th>
                          <th className='text-end'>Atendimentos</th>
                          <th className='text-end'>Tempo medio espera</th>
                          <th className='text-end'>Tempo medio atendimento</th>
                          <th className='text-end'>SLA espera</th>
                          <th className='text-end'>SLA atendimento</th>
                        </tr>
                      </thead>
                      <tbody>
                        {sellerRows.map((row, index) => (
                          <tr key={row.sellerId}>
                            <td>#{index + 1}</td>
                            <td className='fw-bold text-dark'>
                              <SellerIdentity label={row.sellerLabel} />
                            </td>
                            <td className='text-end'>{numberFormatter.format(row.totalSessions)}</td>
                            <td className='text-end'>{formatSeconds(row.avgWaitSeconds)}</td>
                            <td className='text-end'>{formatSeconds(row.avgServiceSeconds)}</td>
                            <td className='text-end fw-semibold text-success'>
                              {percentFormatter.format(row.waitSlaRate)}%
                            </td>
                            <td className='text-end fw-semibold text-info'>
                              {percentFormatter.format(row.serviceSlaRate)}%
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className='alert alert-light-info mb-0'>
                    Nenhuma sessão encontrada para os filtros selecionados.
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </ReportShell>
    </>
  )
}

export { SellerAttendanceSlaReportPage }

